import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { fetchArticles } from "../../../services/apiService";
import { useUserStore } from "../../../store/userStore";
import { useCollectionStore } from "../../../store/collectionStore";
import { useTranslation } from "react-i18next";

export function useNewsArticles() {
  const { selectedCollection } = useCollectionStore();
  const { user } = useUserStore();

  const collectionId = selectedCollection?.id ?? null;
  const userId = user?.firebaseUser?.uid ?? null;

  const { i18n } = useTranslation();
  const language = i18n.language;

  const isArticlesQueryEnabled = Boolean(userId && collectionId);

  const initialPageParam = useMemo(() => ({
    direction: "newer" as const,
    cursor: undefined,
    collectionId: collectionId ?? undefined,
  }), [collectionId]);

  const query = useInfiniteQuery<any>({
    enabled: isArticlesQueryEnabled,
    queryKey: ["articles", language, collectionId, userId],
    queryFn: ({ pageParam }) => fetchArticles(pageParam),  
    initialPageParam,
    // need to move to timesatamp and id based cursor
    getPreviousPageParam: (firstPage, _allPages, _firstPageParam, allPageParams) => {
      const prev = firstPage.prevCursor;
      const firstUsedParam = allPageParams?.[0] as
      | { cursor?: string }
      | undefined;
      const currentCursor = firstUsedParam?.cursor;

      if (!prev || prev === currentCursor) return undefined;

      return {
        direction: "newer",
        cursor: prev,
        collectionId: collectionId ?? undefined,
      };
    },
    // need to move to timesatamp and id based cursor
    getNextPageParam: (lastPage, _allPages, _lastPageParam, allPageParams) => {
    const next = lastPage.nextCursor;
    const lastUsedParam = allPageParams?.[
        allPageParams.length - 1
    ] as { cursor?: string } | undefined;
    const lastUsedCursor = lastUsedParam?.cursor;

      if (!lastPage.hasMore || !next || next === lastUsedCursor) return undefined;

      return {
        direction: "older",
        cursor: next,
        collectionId: collectionId ?? undefined,
      };
    },

    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,

    // 🔑 stop refetching when hasMore = false
    refetchInterval: (data) => {
      const lastPage = data?.state?.data?.pages?.[data?.state?.data?.pages.length - 1];
      return lastPage?.hasMore ? 10 * 60 * 1000 : false;
    },
  });

  return { ...query, isArticlesQueryEnabled };
}